import { useCallback, useEffect, useMemo } from 'react';

import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
  Location,
  User,
  UserRole,
  useUpsertCompanyProfileMutation,
} from 'api/graphql';
import { Switch } from 'components/ui/forms';
import { SwitchLocationContactCompanyProfileSelectors } from 'consts/cypress';
import { useToast } from 'hooks';
import { typeGuards } from 'utils';

import { texts } from './SwitchLocationContactCompanyProfile.text';

type SwitchLocationContactCompanyProfileProps = {
  user: User;
  location: Location;
  disabled?: boolean;
};

export const SwitchLocationContactCompanyProfile = ({
  user,
  location,
  disabled,
}: SwitchLocationContactCompanyProfileProps) => {
  const { formatMessage } = useIntl();
  const { register, setValue } = useForm();
  const { addToast } = useToast();
  const [updateCompanyProfile, { error, loading }] =
    useUpsertCompanyProfileMutation();

  useEffect(() => {
    if (error) {
      addToast({
        title: formatMessage(texts.updateError),
        message: error.message,
        type: 'error',
      });
    }
  }, [addToast, error, formatMessage]);

  const companyProfileLocationIds = useMemo(() => {
    const companyProfile = user.userProfile.map((profile) =>
      typeGuards.companyProfile(profile)
    )[0];
    return companyProfile?.locations.map(({ id }) => id) || [];
  }, [user.userProfile]);

  const onChange = useCallback(
    async ({ target }) => {
      let locations: (string | number)[] = companyProfileLocationIds;

      if (!target.checked) {
        locations = locations.filter(
          (locationId) => locationId !== location.id
        );
      } else {
        locations = [...locations, location.id];
      }

      const { data } = await updateCompanyProfile({
        variables: {
          id: user.id,
          input: {
            company: location.company.id,
            locations,
          },
        },
      });

      if (data?.upsertCompanyProfile) {
        addToast({
          title: formatMessage(texts.updateSuccess),
          type: 'success',
        });
      }
    },
    [
      addToast,
      companyProfileLocationIds,
      formatMessage,
      location.company.id,
      location.id,
      updateCompanyProfile,
      user.id,
    ]
  );

  // defaultChecked needs some help
  useEffect(() => {
    if (user.role === UserRole.CompanyAdmin) {
      return;
    }

    setValue(
      user.id.toString(),
      companyProfileLocationIds.includes(location.id)
    );
  }, [companyProfileLocationIds, location.id, setValue, user.id, user.role]);

  return (
    <div data-cy={SwitchLocationContactCompanyProfileSelectors.Switch}>
      <Switch
        name={user.id.toString()}
        register={register}
        onChange={onChange}
        disabled={disabled || loading || user.role === UserRole.CompanyAdmin}
        defaultChecked={
          user.role === UserRole.CompanyAdmin ||
          companyProfileLocationIds.includes(location.id)
        }
      />
    </div>
  );
};
